import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import SearchableSelect from '../../components/UI/SearchableSelect';
import { workspaceService, CreateWorkspaceRequest, WorkspaceDto } from '../../services/workspaceService';
import { chatbotService } from '../../services/chatbotService';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import { getCustomerIdFromStorage, useCrossTenantSelector } from '../../hooks/useCrossTenantSelector';
import { useAuth } from '../../contexts/AuthContext';
import { canManageCrossCustomerUsers } from '../../utils/ownerCustomerAccess';
import { RunChatbotBatchModal } from './components/RunChatbotBatchModal';
import { extractAccessErrorMessage } from '../../utils/featureAccessError';
import { WorkspaceAvatar } from '../../components/UI/WorkspaceAvatar';

// Estado para workspaces reais

// Função para gerar GUID
const generateGuid = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const ChatbotWorkspacesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const canRunChatbotBatch = canManageCrossCustomerUsers(user);
  const [workspaces, setWorkspaces] = useState<WorkspaceDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newWorkspace, setNewWorkspace] = useState({
    name: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    workspaceId: string | null;
  }>({
    isOpen: false,
    workspaceId: null
  });
  const [duplicateModal, setDuplicateModal] = useState<{
    isOpen: boolean;
    workspace: WorkspaceDto | null;
    targetCustomerId: string;
  }>({
    isOpen: false,
    workspace: null,
    targetCustomerId: ''
  });
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);

  const loadWorkspaces = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await workspaceService.getWorkspaces();

      if (response && response.length > 0) {
        setWorkspaces(response);
      } else {
        setWorkspaces([]);
      }
    } catch (error: unknown) {
      console.error('Erro ao carregar workspaces:', error);
      const accessMessage = extractAccessErrorMessage(error);
      if (accessMessage)
      {
        addToast(accessMessage, 'warning');
      }
      else
      {
        addToast('Erro ao carregar chatbots. Tente novamente.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  const {
    canSelectTenant,
    tenants,
    tenantOptions,
    selectedTenantId,
    handleTenantChange,
    tenantsLoading,
    currentCustomerId,
    isManagingOtherTenant,
  } = useCrossTenantSelector(async tenantId => {
    workspaceService.setActiveTenantId(tenantId === getCustomerIdFromStorage() ? null : tenantId);
    await loadWorkspaces();
  });

  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name;

  useEffect(() => {
    return () => {
      workspaceService.setActiveTenantId(null);
    };
  }, []);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const filteredWorkspaces = workspaces?.filter(workspace =>
    workspace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (workspace.description && workspace.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Debug: mostrar estado atual (apenas em desenvolvimento)
  useEffect(() => {
    if (workspaces.length > 0) {
              // Workspaces loaded
    }
  }, [workspaces]);

  // Debug: mostrar filteredWorkspaces
  useEffect(() => {
            // Filtered workspaces updated
  }, [filteredWorkspaces]);

  const resolveCrossTenantCustomerId = (tenantId: string): string | undefined => {
    if (!tenantId || tenantId === currentCustomerId) {
      return undefined;
    }

    return tenantId;
  };

  const handleRunChatbotBatchConfirm = async (workspaceId: string) => {
    setIsSubmittingBatch(true);
    try {
      const success = await chatbotService.runChatbotBatch(workspaceId);
      if (success) {
        addToast(
          'Indexação em lote enfileirada. Você será notificado ao concluir.',
          'info'
        );
        setShowBatchModal(false);
      }
    } catch (error: any) {
      addToast(error.message || 'Erro ao enfileirar indexação em lote.', 'error');
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspace.name.trim()) {
      addToast('Por favor, insira um nome para o chatbot.', 'warning');
      return;
    }

    if (newWorkspace.name.length > 100) {
      addToast('O nome do chatbot não pode exceder 100 caracteres.', 'warning');
      return;
    }

    setIsCreating(true);
    
    try {
      // Criar workspace na API real
      const request: CreateWorkspaceRequest = {
        name: newWorkspace.name.trim()
      };

      const targetCustomerId = resolveCrossTenantCustomerId(selectedTenantId);
      const newWorkspaceData = await workspaceService.createWorkspace(request, targetCustomerId);
      
      if (!targetCustomerId) {
        setWorkspaces(prev => {
          const newList = [newWorkspaceData, ...(Array.isArray(prev) ? prev : [])];
          return newList;
        });
      } else {
        await loadWorkspaces();
      }
      
      setShowCreateModal(false);
      setNewWorkspace({ name: '' });
      
      addToast('Chatbot criado com sucesso!', 'success');
      
      if (!targetCustomerId) {
        navigate(`/chatbot-admin/workspaces/edit?id=${newWorkspaceData.id}`);
      }
      
    } catch (error) {
      console.error('Erro ao criar workspace:', error);
      
      // Em caso de erro: manter modal aberta e mostrar mensagem
      const errorMessage = error instanceof Error && error.message 
        ? `Erro ao criar chatbot: ${error.message}`
        : 'Erro ao criar chatbot. Verifique sua conexão e tente novamente.';
      
      addToast(errorMessage, 'error');
      
      // NÃO fechar a modal - usuário pode tentar novamente
      // NÃO limpar o formulário - usuário pode corrigir e tentar novamente
      
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenCreateModal = () => {
    setNewWorkspace({ name: '' });
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setNewWorkspace({ name: '' });
  };

  const handleOpenDuplicateModal = (workspace: WorkspaceDto) => {
    setDuplicateModal({
      isOpen: true,
      workspace,
      targetCustomerId: selectedTenantId || currentCustomerId
    });
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModal({
      isOpen: false,
      workspace: null,
      targetCustomerId: ''
    });
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateModal.workspace) {
      return;
    }

    const workspace = duplicateModal.workspace;
    setDuplicatingId(workspace.id);

    try {
      const sourceCustomerId = resolveCrossTenantCustomerId(selectedTenantId);
      const targetCustomerId = resolveCrossTenantCustomerId(duplicateModal.targetCustomerId);
      const targetTenantId = duplicateModal.targetCustomerId || currentCustomerId;
      const viewedTenantId = selectedTenantId || currentCustomerId;

      const clone = await workspaceService.duplicateWorkspace(
        workspace.id,
        sourceCustomerId,
        targetCustomerId
      );

      const isTargetOwnTenant = targetTenantId === currentCustomerId;
      const isTargetViewedTenant = targetTenantId === viewedTenantId;

      if (isTargetViewedTenant) {
        setWorkspaces(prev => {
          const list = Array.isArray(prev) ? prev : [];
          return [clone, ...list];
        });
      }

      addToast(
        isTargetOwnTenant
          ? 'Chatbot duplicado com sucesso!'
          : 'Chatbot duplicado com sucesso no cliente selecionado!',
        'success'
      );

      if (isTargetOwnTenant) {
        navigate(`/chatbot-admin/workspaces/edit?id=${clone.id}`);
      }

      handleCloseDuplicateModal();
    } catch (error) {
      console.error('Erro ao duplicar workspace:', error);
      addToast('Erro ao duplicar chatbot. Tente novamente.', 'error');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDeleteWorkspace = async (id: string) => {
    setDeleteConfirmation({
      isOpen: true,
      workspaceId: id
    });
  };

  const confirmDeleteWorkspace = async () => {
    if (!deleteConfirmation.workspaceId) return;
    
    try {
      await workspaceService.deleteWorkspace(deleteConfirmation.workspaceId);
      setWorkspaces(prev => Array.isArray(prev) ? prev.filter(workspace => workspace.id !== deleteConfirmation.workspaceId) : []);
      addToast('Chatbot excluído com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao excluir workspace:', error);
      addToast('Erro ao excluir chatbot. Tente novamente.', 'error');
    } finally {
      setDeleteConfirmation({
        isOpen: false,
        workspaceId: null
      });
    }
  };

  // Carregar workspaces da API
  // (loadWorkspaces é chamado no mount e ao trocar tenant via useCrossTenantSelector)

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-900 mb-2">
              Gerenciar Chatbots
            </h1>
            <p className="text-gray-600">
              Crie e gerencie seus chatbots e workspaces.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canRunChatbotBatch && (
              <Button
                variant="secondary"
                onClick={() => setShowBatchModal(true)}
              >
                Indexar documentos no chatbot
              </Button>
            )}
            <Button onClick={handleOpenCreateModal}>
              + Novo Chatbot
            </Button>
          </div>
        </div>
      </div>

      {canSelectTenant && (
        <div className="mb-4 w-72 max-w-full">
          <SearchableSelect
            options={tenantOptions}
            value={selectedTenantId || null}
            onChange={handleTenantChange}
            placeholder="Selecione um cliente"
            searchPlaceholder="Buscar cliente..."
            emptyMessage="Nenhum cliente encontrado"
            disabled={tenantsLoading}
            ariaLabel="Selecionar cliente para gerenciar chatbots"
          />
        </div>
      )}

      {isManagingOtherTenant && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>
            Você está visualizando e gerenciando os chatbots do cliente{' '}
            <strong>{selectedTenantName}</strong>. Alterações feitas aqui afetam o ambiente desse cliente.
          </span>
        </div>
      )}

      {/* Barra de Pesquisa */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar chatbots..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

                           {/* Lista de Chatbots */}
        {isLoading ? (
          <div className="bg-white shadow rounded-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando chatbots...</p>
          </div>
        ) : workspaces.length === 0 ? (
          // Mensagem de lista vazia
          <div className="bg-white shadow rounded-md p-12 text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">🤖</span>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Nenhum chatbot encontrado
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm 
                ? `Nenhum chatbot encontrado para "${searchTerm}". Tente ajustar sua busca.`
                : 'Você ainda não criou nenhum chatbot. Comece criando seu primeiro assistente virtual!'
              }
            </p>
            {!searchTerm && (
              <Button onClick={handleOpenCreateModal}>
                + Criar Primeiro Chatbot
              </Button>
            )}
          </div>
                 ) : (
           <div className="bg-white shadow overflow-hidden sm:rounded-md">
             <ul className="divide-y divide-gray-200">
               {filteredWorkspaces.map((workspace) => (
             <li key={workspace.id}>
               <div className="px-6 py-4 hover:bg-gray-50">
                 <div className="flex items-center justify-between">
                   <div className="flex items-center space-x-4">
                     <div className="flex-shrink-0">
                       <WorkspaceAvatar
                         workspaceId={workspace.id}
                         avatar={workspace.avatar}
                         alt={workspace.name}
                         className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center"
                         imgClassName="h-12 w-12 rounded-lg object-cover"
                       >
                         <span className="text-2xl">🤖</span>
                       </WorkspaceAvatar>
                     </div>
                     <div className="flex-1 min-w-0">
                       <div className="flex items-center space-x-2">
                         <h3 className="text-lg font-medium text-gray-900 truncate">
                           {workspace.name}
                         </h3>
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                           {workspace.isActive ? 'Ativo' : 'Inativo'}
                         </span>
                       </div>
                       <p className="text-sm text-gray-500 mt-1">
                         {workspace.description || 'Sem descrição'}
                       </p>
                       <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                         <span>Slug: {workspace.slug}</span>
                         <span>Assistente: {workspace.assistantName}</span>
                         <span>Provedor: {workspace.chatProvider || 'N/A'}</span>
                         <span>Top N: {workspace.topN || 'N/A'}</span>
                         <span>Histórico: {workspace.aiHistory || 'N/A'}</span>
                       </div>
                     </div>
                   </div>
                   <div className="flex items-center space-x-4">
                     <div className="text-right">
                       <div className="text-sm font-medium text-gray-900">
                         -
                       </div>
                       <div className="text-xs text-gray-500">mensagens</div>
                     </div>
                     <div className="flex space-x-2">
                       <Button
                         variant="secondary"
                         size="sm"
                         onClick={() => handleOpenDuplicateModal(workspace)}
                         disabled={duplicatingId === workspace.id}
                         title="Duplicar chatbot"
                       >
                         <DocumentDuplicateIcon className="h-5 w-5" />
                       </Button>
                       <Button 
                         variant="secondary"
                         size="sm"
                         onClick={() => navigate(`/chatbot-admin/workspaces/edit?id=${workspace.id}`)}
                         title="Editar chatbot"
                       >
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                         </svg>
                       </Button>
                       <Button 
                         variant="danger"
                         size="sm"
                         onClick={() => handleDeleteWorkspace(workspace.id)}
                         title="Excluir chatbot"
                       >
                         <TrashIcon className="h-5 w-5" />
                       </Button>
                     </div>
                   </div>
                 </div>
               </div>
             </li>
           ))}
         </ul>
         </div>
       )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                Criar Novo Chatbot
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome * (máx. 100 caracteres)
                  </label>
                  <input
                    type="text"
                    id="workspace-name"
                    value={newWorkspace.name}
                    onChange={(e) => setNewWorkspace(prev => ({ ...prev, name: e.target.value }))}
                    maxLength={100}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      newWorkspace.name.length > 100 
                        ? 'border-red-300 focus:ring-red-500' 
                        : newWorkspace.name.length > 80 
                          ? 'border-yellow-300 focus:ring-yellow-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Digite o nome do chatbot"
                    autoFocus
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs ${
                      newWorkspace.name.length > 100 
                        ? 'text-red-600' 
                        : newWorkspace.name.length > 80 
                          ? 'text-yellow-600' 
                          : 'text-gray-500'
                    }`}>
                      {newWorkspace.name.length}/100 caracteres
                    </span>
                    {newWorkspace.name.length > 100 && (
                      <span className="text-xs text-red-600 font-medium">
                        Limite excedido!
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  onClick={handleCloseModal}
                  variant="secondary"
                  disabled={isCreating}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateWorkspace}
                  disabled={isCreating || !newWorkspace.name.trim() || newWorkspace.name.length > 100}
                >
                  {isCreating ? 'Criando...' : 'Criar Chatbot'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {duplicateModal.isOpen && duplicateModal.workspace && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">
                Duplicar Chatbot
              </h3>
              <p className="text-sm text-gray-600 mb-4 text-center">
                Tem certeza que deseja duplicar <strong>{duplicateModal.workspace.name}</strong>?
              </p>

              {canSelectTenant && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente destino
                  </label>
                  <SearchableSelect
                    options={tenantOptions}
                    value={duplicateModal.targetCustomerId || null}
                    onChange={(targetCustomerId: string) =>
                      setDuplicateModal(prev => ({
                        ...prev,
                        targetCustomerId
                      }))
                    }
                    placeholder="Selecione um cliente"
                    searchPlaceholder="Buscar cliente..."
                    emptyMessage="Nenhum cliente encontrado"
                    disabled={tenantsLoading || duplicatingId === duplicateModal.workspace.id}
                    ariaLabel="Selecionar cliente destino da duplicação"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  onClick={handleCloseDuplicateModal}
                  variant="secondary"
                  disabled={duplicatingId === duplicateModal.workspace.id}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmDuplicate}
                  disabled={
                    duplicatingId === duplicateModal.workspace.id ||
                    (canSelectTenant && !duplicateModal.targetCustomerId)
                  }
                >
                  {duplicatingId === duplicateModal.workspace.id ? 'Duplicando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, workspaceId: null })}
        onConfirm={confirmDeleteWorkspace}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este chatbot? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <RunChatbotBatchModal
        open={showBatchModal}
        customerId={resolveCrossTenantCustomerId(selectedTenantId)}
        isManagingOtherTenant={isManagingOtherTenant}
        tenantName={selectedTenantName}
        onClose={() => !isSubmittingBatch && setShowBatchModal(false)}
        onConfirm={handleRunChatbotBatchConfirm}
        isSubmitting={isSubmittingBatch}
      />
    </div>
  );
};

export default ChatbotWorkspacesPage;
